import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import {environment} from '../../environments/environment';
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {FormsModule} from "@angular/forms";
import {InfoBox} from "../info-box/info-box";
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatFooterCellDef,
  MatFooterRow,
  MatFooterRowDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource
} from "@angular/material/table";
import {TermsBox} from "../terms-box/terms-box";
import {DateTime} from 'luxon';
import {LoaSite} from '../models/LoaSite';
import {MeterSubmission} from '../models/LoaSubmission';
import {LoaService} from '../service/loa/loa';
import {ActivatedRoute} from '@angular/router';
import {Routing} from '../service/routing/routing';
import {BrandDetails, Loa, SubmittedLoa} from '../models/Loa';
import {BrandingHeader} from '../branding-header/branding-header';
import {LoadingSpinner} from '../loading-spinner/loading-spinner';
import {PdfService} from '../service/pdf/pdf';
import {DatePicker} from '../date-picker/date-picker';
import {catchError, EMPTY} from 'rxjs';
import {Footer} from '../footer/footer';
import {NgClass} from '@angular/common';

@Component({
  selector: 'app-loa-form',
  imports: [
    FaIconComponent,
    FormsModule,
    InfoBox,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable,
    TermsBox,
    BrandingHeader,
    MatHeaderCellDef,
    MatFooterCellDef,
    LoadingSpinner,
    DatePicker,
    Footer,
    NgClass,
  ],
  templateUrl: './loa-form.html',
  styleUrl: './loa-form.scss',
})
export class LoaForm implements OnInit {

  private cdr = inject(ChangeDetectorRef);
  private pdfService = inject(PdfService);
  private elementRef = inject(ElementRef);
  public loaService = inject(LoaService);
  private route = inject(ActivatedRoute);
  private routing = inject(Routing);

  confirmAccountOwner = false;
  authorizeProcessing = false;
  agreePrivacyPolicy = false;

  @Input() loa!: Loa;
  @Output() submittedLoa: EventEmitter<SubmittedLoa> = new EventEmitter<SubmittedLoa>();
  @Output() pdfGenerated: EventEmitter<File> = new EventEmitter<File>();
  @Input() brandDetails!: BrandDetails | null;
  @Input() token!: string;

  isSubmitting = signal(false);
  readonly privacyPolicyLink = environment.PRIVACY_POLICY;
  readonly termsLink = 'assets/latest.md';
  uploadedFiles: File[] = [];

  meterEnabledState: Map<string, boolean> = new Map();
  enabledCount: number = 0;
  readonly fallbackDate = DateTime.now().minus({ months: 36 }).toISODate()!;

  dataSource: MatTableDataSource<LoaSite> = new MatTableDataSource<LoaSite>();

  displayedColumns: string[] = [
    'building',
    'consentDate',
    'meters',
  ];

  /** `true` when every meter on the LOA is a renewal, which switches the info box copy. */
  isRenewal = false;

  /** Initialises the meter table and meter enabled states on component load. */
  ngOnInit(): void {
    this.isRenewal = this.loaService.isRenewalLoa(this.loa);
    this.setUpTable();
    this.initializeMeterStates();
    this.cdr.detectChanges();
  }

  /**
   * Groups meters from the LOA by building name and determines the consent date for each site.
   * If all meters in a building have a known `dataStart`, the earliest is used and the date is
   * locked. If none have a `dataStart`, the date defaults to 36 months ago and is left editable.
   */
  setUpTable() {


    
    const fallbackDate = this.fallbackDate;

    const sites = this.loa.meters.reduce((acc, meter) => {
      const bName = meter.buildingName;
      if (!acc[bName]) {
        acc[bName] = {
          buildingName: bName,
          meters: [],
          consentDate: fallbackDate,
          isDateEditable: false,
        };
      }

      acc[bName].meters.push(meter);
      return acc;
    }, {} as { [key: string]: LoaSite });

    for (const site of Object.values(sites)) {
      const nonNullDates = site.meters
        .map(m => m.dataStart)
        .filter((d): d is string => !!d);

      if (nonNullDates.length === 0) {
        site.consentDate = fallbackDate;
        site.isDateEditable = true;
      } else {
        site.consentDate = nonNullDates.reduce((earliest, date) =>
          DateTime.fromISO(date) < DateTime.fromISO(earliest) ? date : earliest
        );
        site.isDateEditable = false;
      }
    }

    this.dataSource.data = Object.values(sites);
  }

  /** Sets every meter to enabled and syncs `enabledCount` with the total meter count. */
  initializeMeterStates(): void {
    this.loa.meters.forEach(meter => {
      this.meterEnabledState.set(meter.meterUuid, true);
    });
    this.enabledCount = this.meterEnabledState.size;
  }

  /**
   * Toggles the enabled/disabled state of a meter and updates `enabledCount` accordingly.
   * @param meterUuid : string - The UUID of the meter to toggle.
   */
  toggleMeterState(meterUuid: string): void {
    const currentState = this.meterEnabledState.get(meterUuid) ?? true;
    this.meterEnabledState.set(meterUuid, !currentState);
    if (currentState == true) {
      this.enabledCount--;
    } else {
      this.enabledCount++;
    }
  }

  /**
   * Handles files selected via the file input element, adding them to `uploadedFiles`.
   * Resets the input value so the same file can be re-selected if needed.
   * @param event : Event - The change event from the file input.
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.addFiles(input.files);
    }
    input.value = '';
  }

  /**
   * Prevents the default browser drag-over behaviour to allow the drop event to fire.
   * @param event : DragEvent - The dragover event.
   */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    // You can add logic here to visually indicate dragging over the area
  }

  /**
   * Handles files dropped onto the upload area, adding them to `uploadedFiles`.
   * @param event : DragEvent - The drop event containing the transferred files.
   */
  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files: FileList | null = event.dataTransfer?.files || null;
    if (files) {
      this.addFiles(files);
    }
  }

  private addFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      this.uploadedFiles.push(files[i]);
    }
  }

  /**
   * Removes an uploaded file by its index in `uploadedFiles`.
   * @param index : number - Zero-based index of the file to remove.
   */
  removeFile(index: number) {
    this.uploadedFiles.splice(index, 1);
  }

  /**
   * Validates the form, generates the LOA PDF, then submits the signatory details,
   * meter selections, and all documents (uploaded files + PDF) to the API.
   * Emits `pdfGenerated` with the PDF file and `submittedLoa` on success.
   * Shows an alert and resets `isSubmitting` on API error.
   */
  async submit() {
    if (!this.isFormValid) {
      return;
    }
    this.isSubmitting.set(true);

    const meterSubmissions: MeterSubmission[] = this.loa.meters.map(meter => {
      const isAccepted = this.meterEnabledState.get(meter.meterUuid) ?? false;
      const site = this.dataSource.data.find(s => s.buildingName === meter.buildingName);
      return {
        meterUuid: meter.meterUuid,
        isAccepted: isAccepted,
        dataStart: isAccepted ? site?.consentDate || null : null
      };
    });

    const signatory = {
      name: this.loa.signatory.name ?? '',
      company: this.loa.signatory.company ?? '',
      jobTitle: this.loa.signatory.jobTitle ?? '',
      email: this.loa.signatory.email,
    };
    const pdfFile = await this.pdfService.generateFile(this.elementRef.nativeElement);
    this.pdfGenerated.emit(pdfFile);
    const filesToSubmit = [...this.uploadedFiles, pdfFile];

    this.loaService.submitLoa(this.token, signatory, meterSubmissions, filesToSubmit)
      .pipe(catchError(e => {
        console.log(e);
        this.isSubmitting.set(false);
        alert("Failed to submit, please try again.");
        return EMPTY;
      })).subscribe(response => {
      if (response != null) {
        this.isSubmitting.set(false);
        this.submittedLoa.emit(response);
      }
      this.cdr.detectChanges();
    });
  }

  /**
   * Returns `true` when the signatory name, company, and job title are all non-empty
   * and all three consent checkboxes are checked.
   */
  get isFormValid(): boolean {
    const hasSelectedMeter = [...this.meterEnabledState.values()].some(enabled => enabled);
    return !!this.loa?.signatory?.name?.trim() &&
      !!this.loa?.signatory?.company?.trim() &&
      !!this.loa?.signatory?.jobTitle?.trim() &&
      this.confirmAccountOwner &&
      this.authorizeProcessing &&
      this.agreePrivacyPolicy &&
      hasSelectedMeter;
  }
}
